import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  sanitizeVapBitrateKbps,
  sanitizeVapFps,
  sanitizeVapMaxDurationSec,
  VAP_BITRATE_KBPS_MAX,
  VAP_BITRATE_KBPS_MIN,
  VAP_FPS_MAX,
  VAP_FPS_MIN,
  VAP_MAX_DURATION_SEC_MAX,
  VAP_MAX_DURATION_SEC_MIN,
  type VapEncodeLimits,
} from "../export/components/export-vap/vapConfig";
import {
  getVapOriginalDimensions,
  getVapPreviewDimensions,
} from "../../utils/fullscreenType";

export interface VapEncodeSettingsProps {
  value: VapEncodeLimits;
  onChange: (next: VapEncodeLimits) => void;
  fullscreenType?: number;
  disabled?: boolean;
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  disabled,
  onCommit,
  hint,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onCommit: (n: number) => void;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onCommit(Number(e.target.value))}
        disabled={disabled}
      />
      {hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

/** 创建 Avatar 弹窗内的 VAP 编码高级设置（分辨率固定不配） */
export function VapEncodeSettings({
  value,
  onChange,
  fullscreenType,
  disabled,
}: VapEncodeSettingsProps) {
  const originalDims = getVapOriginalDimensions(fullscreenType);
  const previewDims = getVapPreviewDimensions(fullscreenType);

  const patch = (partial: Partial<VapEncodeLimits>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="vap-encode" className="border-none">
        <AccordionTrigger className="py-2 text-sm text-muted-foreground hover:no-underline">
          VAP 视频参数（高级）
        </AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-2 gap-6 pt-1">
            <div className="space-y-3">
              <p className="text-xs font-medium text-foreground">
                原图（{originalDims.outputWidth}×{originalDims.outputHeight}，固定）
              </p>
              <NumberField
                id="original-fps"
                label="FPS"
                value={value.originalFps}
                min={VAP_FPS_MIN}
                max={VAP_FPS_MAX}
                disabled={disabled}
                onCommit={(n) => patch({ originalFps: sanitizeVapFps(n) })}
              />
              <NumberField
                id="original-bitrate"
                label="码率（kbps）"
                value={value.originalBitrateKbps}
                min={VAP_BITRATE_KBPS_MIN}
                max={VAP_BITRATE_KBPS_MAX}
                disabled={disabled}
                onCommit={(n) =>
                  patch({ originalBitrateKbps: sanitizeVapBitrateKbps(n) })
                }
              />
              <NumberField
                id="original-max-duration"
                label="最长时长（秒）"
                value={value.originalMaxDurationSec}
                min={VAP_MAX_DURATION_SEC_MIN}
                max={VAP_MAX_DURATION_SEC_MAX}
                disabled={disabled}
                onCommit={(n) =>
                  patch({
                    originalMaxDurationSec: sanitizeVapMaxDurationSec(n),
                  })
                }
                hint="实际为 min(动画时长, 此值)"
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-medium text-foreground">
                缩略图（{previewDims.outputWidth}×{previewDims.outputHeight}，固定）
              </p>
              <NumberField
                id="preview-fps"
                label="FPS"
                value={value.previewFps}
                min={VAP_FPS_MIN}
                max={VAP_FPS_MAX}
                disabled={disabled}
                onCommit={(n) => patch({ previewFps: sanitizeVapFps(n) })}
              />
              <NumberField
                id="preview-bitrate"
                label="码率（kbps）"
                value={value.previewBitrateKbps}
                min={VAP_BITRATE_KBPS_MIN}
                max={VAP_BITRATE_KBPS_MAX}
                disabled={disabled}
                onCommit={(n) =>
                  patch({ previewBitrateKbps: sanitizeVapBitrateKbps(n) })
                }
              />
              <NumberField
                id="preview-max-duration"
                label="最长时长（秒）"
                value={value.previewMaxDurationSec}
                min={VAP_MAX_DURATION_SEC_MIN}
                max={VAP_MAX_DURATION_SEC_MAX}
                disabled={disabled}
                onCommit={(n) =>
                  patch({
                    previewMaxDurationSec: sanitizeVapMaxDurationSec(n),
                  })
                }
                hint="实际为 min(动画时长, 此值)"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
